import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useLogin, parseError } from '../lib/resources/auth';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useState } from 'react';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const loginSchema = z.object({
  email: z.string().min(1, 'Required').refine((v) => v.includes('@'), 'Must contain @'),
  password: z.string().min(1, 'Required'),
});
type LoginValues = z.infer<typeof loginSchema>;

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const login = useLogin();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginValues) {
    setServerError(null);
    try {
      await login.mutateAsync(values);
      // Re-run all loaders so the dashboard pulls /me + /periods with the new session.
      await router.invalidate();
      void navigate({ to: '/' });
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Login failed');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <Card className="w-full max-w-[440px]">
        <CardHeader>
          <CardTitle>Sign in to SuSaInvoicer</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="text"
                inputMode="email"
                autoComplete="username"
                autoFocus
                {...form.register('email')}
              />
              {form.formState.errors.email?.message && (
                <div className="text-[11px] text-error mt-1">{form.formState.errors.email.message}</div>
              )}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
              {form.formState.errors.password?.message && (
                <div className="text-[11px] text-error mt-1">{form.formState.errors.password.message}</div>
              )}
            </div>
            {serverError && (
              <div className="text-[12px] text-error" role="alert">{serverError}</div>
            )}
            <Button type="submit" disabled={login.isPending} className="w-full">
              {login.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Sign in
            </Button>
          </form>
          <p className="mt-6 text-[11px] text-foreground-muted text-center">
            Lost your password? Ask an admin to reset it.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
